/**
 * 🍦 Layout Component
 * App shell with bottom navigation + floating order status
 */

import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { IceCream, Map, Navigation, User, Clock, MapPin, Sparkles } from 'lucide-react';
import { useOrderStore } from '../store';
import styles from './Layout.module.css';

const navItems = [
  { path: '/menu', icon: IceCream, label: 'Menu' },
  { path: '/map', icon: Map, label: 'Map' },
  { path: '/navigate', icon: Navigation, label: 'Navigate' },
  { path: '/fun', icon: Sparkles, label: 'Fun' },
  { path: '/profile', icon: User, label: 'Me' },
];

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderHistory = useOrderStore((state) => state.orderHistory);

  // Find active order (not collected)
  const activeOrder = orderHistory.find(
    (o) => o.status === 'pending' || o.status === 'preparing' || o.status === 'ready'
  );

  // Hide nav on certain pages
  const hideNav = ['/cart', '/checkout', '/emergency'].some(p =>
    location.pathname.startsWith(p)
  ) || location.pathname.startsWith('/order/');

  // Don't show order banner on order status page itself
  const isOnOrderPage = location.pathname.startsWith('/order/');

  return (
    <div className={styles.layout}>
      <main className={styles.main}>
        <Outlet />
      </main>

      {/* Floating Order Status Banner */}
      {!hideNav && activeOrder && !isOnOrderPage && (
        <button
          className={`${styles.orderBanner} ${activeOrder.status === 'ready' ? styles.orderBannerReady : ''}`}
          onClick={() => navigate(`/order/${activeOrder.id}`)}
        >
          <div className={styles.orderBannerIcon}>
            {activeOrder.status === 'ready' ? <MapPin size={20} /> : <Clock size={20} />}
          </div>
          <div className={styles.orderBannerInfo}>
            <span className={styles.orderBannerTitle}>
              {activeOrder.status === 'ready'
                ? activeOrder.isDelivery
                  ? `🚀 On the way to ${activeOrder.seatInfo?.section || 'your seat'}!`
                  : `🎉 Ready at Window ${activeOrder.pickupWindow}!`
                : activeOrder.status === 'preparing'
                  ? '🍦 Making your order...'
                  : '✓ Order confirmed'}
            </span>
            <span className={styles.orderBannerSub}>
              Order #{activeOrder.orderNumber} • {activeOrder.isDelivery ? 'Delivery' : 'Pickup'} • Tap to view
            </span>
          </div>
          <span className={styles.orderBannerArrow}>›</span>
        </button>
      )}

      {!hideNav && (
        <nav className={styles.nav}>
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <Icon size={24} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
